import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { systemMessagesApi } from "../api/systemMessages";
import { KEYS } from "../hooks/useSystemMessages";
import type { PendingSystemMessage } from "../types";

// Jak często pytamy o nowe wiadomości. Wiadomość ma trafić na ekran szybko, ale
// to zapytanie płaci KAŻDA otwarta karta każdego pracownika — 30 s to kompromis.
const POLL_MS = 30_000;

function formatSentAt(value: string): string {
  return new Date(value).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Blokujące okno wiadomości systemowych — montowane raz w ProtectedLayout.
 *
 *  Nie da się go zamknąć: nie ma krzyżyka, Escape nic nie robi, kliknięcie w tło
 *  nic nie robi. Przycisk „Oznacz jako przeczytane" odblokowuje się dopiero po
 *  odliczeniu czasu wyświetlania (domyślnie 30 s) — a backend i tak sprawdza to
 *  ponownie, więc pominięcie okna przez samo wywołanie API nic nie daje.
 *  Odliczanie stoi, gdy karta jest w tle: chodzi o to, żeby ktoś REALNIE
 *  zobaczył wiadomość, a nie o to, żeby minęło 30 s w schowanej karcie.
 *
 *  KILKA wiadomości naraz = JEDNO okno, JEDNO odliczanie i JEDNO potwierdzenie —
 *  pięć wiadomości nie może kosztować pracownika pięciu razy po 30 s. Partia jest
 *  zamrażana w chwili otwarcia okna: wiadomość, która przyjdzie w trakcie, czeka
 *  na następne okno (ze swoim własnym odliczaniem), zamiast przedłużać bieżące. */
export function SystemMessagePopupHost() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  // Podwykonawcy są poza tą funkcją (backend i tak nie zrobi ich odbiorcami).
  const eligible = !!user && user.role !== "contractor";

  const { data } = useQuery({
    queryKey: KEYS.pending,
    queryFn: systemMessagesApi.pending,
    enabled: eligible,
    refetchInterval: POLL_MS,
  });

  const pending = useMemo(() => data ?? [], [data]);
  const pendingKey = pending.map((m) => m.id).join(",");

  const [batchIds, setBatchIds] = useState<string[]>([]);
  useEffect(() => {
    setBatchIds((previous) => {
      const stillWaiting = previous.filter((id) =>
        pending.some((m) => m.id === id),
      );
      // Partia w toku zostaje nietknięta — nowe wiadomości poczekają na swoją kolej.
      if (stillWaiting.length > 0) return stillWaiting;
      return pending.map((m) => m.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey]);

  const batch: PendingSystemMessage[] = useMemo(
    () => pending.filter((m) => batchIds.includes(m.id)),
    [pending, batchIds],
  );
  const batchKey = batch.map((m) => m.id).join(",");
  // Wspólne odliczanie dla całej partii — najdłuższy czas, jakiego żąda
  // którakolwiek z pokazanych wiadomości.
  const displaySeconds = batch.reduce(
    (longest, m) => Math.max(longest, m.display_seconds),
    0,
  );

  // Odliczanie jest przypięte do partii, dla której je uzbrojono. Dopóki klucze
  // się nie zgadzają, przycisk jest zablokowany — inaczej nowa partia mignęłaby
  // przez jedno przerysowanie z odblokowanym przyciskiem.
  const [countdown, setCountdown] = useState({ key: "", left: 0 });

  useEffect(() => {
    if (!batchKey) return;
    setCountdown({ key: batchKey, left: displaySeconds });
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setCountdown((c) =>
        c.key === batchKey && c.left > 0 ? { ...c, left: c.left - 1 } : c,
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [batchKey, displaySeconds]);

  // Strona pod spodem nie może się przewijać, dopóki okno jest na ekranie.
  useEffect(() => {
    if (!batchKey) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [batchKey]);

  const acknowledge = useMutation({
    mutationFn: (ids: string[]) => systemMessagesApi.acknowledge(ids),
    onSuccess: (_result, ids) => {
      // Zdejmij potwierdzone od razu, żeby ewentualna kolejna partia pojawiła
      // się bez migotania.
      qc.setQueryData<PendingSystemMessage[]>(KEYS.pending, (old) =>
        (old ?? []).filter((m) => !ids.includes(m.id)),
      );
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
    onError: () => {
      // Backend uznał, że okno nie wisiało jeszcze dość długo (np. po
      // przeładowaniu strony). Daj mu dogonić zegar i pozwól spróbować ponownie.
      setCountdown((c) => ({ ...c, left: 3 }));
    },
  });

  if (batch.length === 0) return null;

  const secondsLeft = countdown.key === batchKey ? countdown.left : displaySeconds;
  const locked = secondsLeft > 0 || countdown.key !== batchKey;
  const many = batch.length > 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/70 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="system-message-heading"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col border-2 border-[var(--color-primary)] bg-[var(--color-bg-surface)] shadow-2xl">
        <div className="flex items-start gap-3 border-b border-[var(--color-border)] px-6 py-4">
          <AlertTriangle
            size={20}
            className="mt-0.5 shrink-0 text-[var(--color-primary)]"
          />
          <div className="min-w-0">
            <p
              id="system-message-heading"
              className="text-xs font-mono uppercase tracking-wider text-[var(--color-primary)]"
            >
              {many
                ? `Wiadomości systemowe (${batch.length})`
                : "Wiadomość systemowa"}
            </p>
            {!many && (
              <>
                <h2 className="mt-1 text-lg font-bold text-[var(--color-text-primary)]">
                  {batch[0].title}
                </h2>
                <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                  {batch[0].sender_name ?? "GK ELEKTIM"} ·{" "}
                  {formatSentAt(batch[0].created_at)}
                </p>
              </>
            )}
            {many && (
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                Zapoznaj się ze wszystkimi — potwierdzasz je jednym kliknięciem.
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!many ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-primary)]">
              {batch[0].body}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {batch.map((message) => (
                <article
                  key={message.id}
                  className="border-l-2 border-[var(--color-primary)] pl-3"
                >
                  <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
                    {message.title}
                  </h2>
                  <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                    {message.sender_name ?? "GK ELEKTIM"} ·{" "}
                    {formatSentAt(message.created_at)}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-primary)]">
                    {message.body}
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-6 py-4">
          <span className="text-xs text-[var(--color-text-secondary)]">
            {locked
              ? `Przycisk odblokuje się za ${secondsLeft} s`
              : "Potwierdź, aby wrócić do pracy"}
          </span>
          <Button
            type="button"
            disabled={locked}
            loading={acknowledge.isPending}
            onClick={() => acknowledge.mutate(batch.map((m) => m.id))}
          >
            {many
              ? `Oznacz jako przeczytane (${batch.length})`
              : "Oznacz jako przeczytane"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
